import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Copyright, Scale, FileText } from 'lucide-react';

export default function CopyrightPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate(-1)} variant="ghost" size="sm" className="gap-2 dark:text-slate-200">
              <ArrowLeft className="w-4 h-4" />
              Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-2">
              <img src="/kai-logo.png" alt="KAI" className="w-12 h-12 object-contain rounded-lg" />
              <span className="font-heading text-primary-900 dark:text-slate-100 font-bold">KAI</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <Copyright className="w-6 h-6 text-primary-600" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl text-primary-900">Prawa Własności Intelektualnej</h1>
          </div>
          <p className="text-muted-foreground mb-8 ml-16">Ostatnia aktualizacja: 7 lutego 2026 r.</p>
          
          <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed">
            
            {/* Oświadczenie autorskie */}
            <section className="bg-gradient-to-r from-primary-50 to-orange-50 p-6 rounded-xl border border-primary-100">
              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 mb-2">Oświadczenie o Prawach Autorskich</h2>
                  <p className="text-slate-700">
                    Serwis <strong>KAI (Kurdynowski AI)</strong> wraz z całą jego zawartością stanowi własność intelektualną 
                    <strong> Moniki Małgorzaty Gawkowskiej</strong> i jest chroniony przepisami prawa autorskiego 
                    Rzeczypospolitej Polskiej oraz umowami międzynarodowymi.
                  </p>
                </div>
              </div>
            </section>

            {/* Zakres ochrony */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">1. Zakres Ochrony Prawnej</h2>
              <p className="mb-4">Ochronie prawnoautorskiej podlegają w szczególności:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-primary-800 mb-2">Elementy techniczne:</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-400">
                    <li>kod źródłowy aplikacji</li>
                    <li>architektura systemu</li>
                    <li>algorytmy i logika biznesowa</li>
                    <li>baza danych i jej struktura</li>
                    <li>interfejsy API</li>
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-primary-800 mb-2">Elementy wizualne:</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-400">
                    <li>interfejs użytkownika (UI)</li>
                    <li>projekt graficzny (UX)</li>
                    <li>logo i znaki graficzne</li>
                    <li>ikony i ilustracje</li>
                    <li>układ i kompozycja stron</li>
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-primary-800 mb-2">Elementy tekstowe:</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-400">
                    <li>nazwa "KAI" i "Kurdynowski AI"</li>
                    <li>treści opisowe i pomocnicze</li>
                    <li>dokumentacja użytkownika</li>
                    <li>materiały marketingowe</li>
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-primary-800 mb-2">Elementy funkcjonalne:</h3>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-400">
                    <li>mechanizmy działania asystenta AI</li>
                    <li>system promptów i instrukcji</li>
                    <li>baza wiedzy i jej opracowanie</li>
                    <li>unikalne rozwiązania techniczne</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Autor */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">2. Autor i Właściciel Praw</h2>
              <div className="bg-primary-50 p-6 rounded-xl border border-primary-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center">
                    <FileText className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-primary-900">Monika Małgorzata Gawkowska</h3>
                    <p className="text-slate-600 dark:text-slate-400">Autor, twórca i właściciel praw autorskich do serwisu KAI</p>
                  </div>
                </div>
                <p className="text-slate-700">
                  Wszystkie majątkowe i osobiste prawa autorskie do serwisu KAI (Kurdynowski AI) przysługują wyłącznie 
                  Monice Małgorzacie Gawkowskiej jako jedynemu autorowi i twórcy dzieła w rozumieniu ustawy z dnia 
                  4 lutego 1994 r. o prawie autorskim i prawach pokrewnych.
                </p>
              </div>
            </section>

            {/* Licencja użytkownika */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">3. Licencja Użytkownika</h2>
              <ol className="list-decimal pl-6 space-y-3">
                <li>Poprzez wykupienie subskrypcji, Użytkownik otrzymuje <strong>ograniczoną, niewyłączną, nieprzenoszalną i odwołalną licencję</strong> na korzystanie z serwisu KAI wyłącznie do celów osobistych lub zawodowych zgodnie z Regulaminem.</li>
                <li>Licencja obejmuje prawo do:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>korzystania z funkcjonalności serwisu zgodnie z jego przeznaczeniem,</li>
                    <li>zapisywania i przeglądania historii konwersacji,</li>
                    <li>pobierania faktur i dokumentów wygenerowanych w serwisie.</li>
                  </ul>
                </li>
                <li>Licencja <strong>nie obejmuje</strong> prawa do:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>kopiowania, modyfikowania lub dekompilacji kodu źródłowego,</li>
                    <li>tworzenia dzieł pochodnych,</li>
                    <li>sublicencjonowania lub odsprzedaży dostępu,</li>
                    <li>wykorzystywania serwisu do celów komercyjnych innych niż własna działalność.</li>
                  </ul>
                </li>
              </ol>
            </section>

            {/* Zakazy */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">4. Działania Zabronione</h2>
              <p className="mb-4">Bez wyraźnej pisemnej zgody Autora zabronione jest:</p>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <ul className="space-y-2 text-red-800">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Kopiowanie, reprodukowanie lub powielanie jakichkolwiek elementów serwisu
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Modyfikowanie, adaptowanie lub tworzenie dzieł pochodnych
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Dekompilacja, inżynieria wsteczna lub próby uzyskania kodu źródłowego
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Usuwanie lub modyfikowanie oznaczeń praw autorskich
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Publiczne udostępnianie, dystrybucja lub sprzedaż
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Wykorzystywanie logo, nazwy lub znaków towarowych bez autoryzacji
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Automatyczne pobieranie danych (scraping, crawling)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">✗</span>
                    Wykorzystywanie serwisu do szkolenia konkurencyjnych modeli AI
                  </li>
                </ul>
              </div>
            </section>

            {/* Znaki towarowe */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">5. Znaki Towarowe</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>"KAI"</strong>, <strong>"Kurdynowski AI"</strong>, logo serwisu oraz inne znaki graficzne używane w serwisie są znakami towarowymi należącymi do Moniki Małgorzaty Gawkowskiej.</li>
                <li>Używanie znaków towarowych bez pisemnej zgody właściciela jest zabronione.</li>
                <li>Nieuprawnione użycie znaków towarowych może skutkować odpowiedzialnością cywilną i karną.</li>
              </ol>
            </section>

            {/* Treści użytkownika */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">6. Treści Generowane przez Użytkownika</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Pytania zadawane przez Użytkownika w ramach konwersacji z asystentem KAI pozostają własnością Użytkownika.</li>
                <li>Korzystając z serwisu, Użytkownik udziela Autorowi nieodpłatnej licencji na:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>przechowywanie treści w ramach świadczenia usługi,</li>
                    <li>anonimową analizę w celach ulepszania serwisu,</li>
                    <li>wykorzystanie zanonimizowanych danych do celów statystycznych.</li>
                  </ul>
                </li>
                <li>Odpowiedzi generowane przez asystenta KAI:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>stanowią własność Autora serwisu,</li>
                    <li>Użytkownik otrzymuje licencję na ich wykorzystanie do celów osobistych i zawodowych,</li>
                    <li>zabrania się masowego kopiowania odpowiedzi w celu tworzenia konkurencyjnych baz wiedzy.</li>
                  </ul>
                </li>
              </ol>
            </section>

            {/* Materiały osób trzecich */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">7. Materiały Osób Trzecich</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Serwis KAI może zawierać odwołania do przepisów prawa, orzeczeń sądowych i innych materiałów urzędowych, które są dostępne publicznie.</li>
                <li>Autor nie rości sobie praw własności do tych materiałów i cytuje je zgodnie z zasadami dozwolonego użytku (art. 29 ustawy o prawie autorskim).</li>
                <li>Wykorzystane technologie AI (OpenAI, Anthropic, Google) są używane na podstawie odpowiednich licencji i umów z ich dostawcami.</li>
              </ol>
            </section>

            {/* Odpowiedzialność */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">8. Odpowiedzialność za Naruszenia</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Naruszenie praw autorskich lub własności intelektualnej może skutkować:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>natychmiastowym zablokowaniem dostępu do serwisu,</li>
                    <li>odpowiedzialnością odszkodowawczą,</li>
                    <li>odpowiedzialnością karną (art. 115-123 ustawy o prawie autorskim).</li>
                  </ul>
                </li>
                <li>W przypadku stwierdzenia naruszenia, Autor podejmie wszelkie dostępne środki prawne w celu ochrony swoich praw.</li>
              </ol>
            </section>

            {/* Zgłaszanie naruszeń */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">9. Zgłaszanie Naruszeń</h2>
              <p className="mb-4">W przypadku stwierdzenia naruszenia praw własności intelektualnej serwisu KAI lub podejrzenia takiego naruszenia, prosimy o kontakt:</p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-slate-700">
                  <strong>E-mail:</strong> monika.gawkowska@kurdynowski.pl<br />
                  <strong>Temat:</strong> Zgłoszenie naruszenia praw autorskich
                </p>
              </div>
              <p className="mt-4">Zgłoszenie powinno zawierać: opis naruszenia, lokalizację naruszenia (URL), dane kontaktowe zgłaszającego.</p>
            </section>

            {/* Postanowienia końcowe */}
            <section>
              <h2 className="font-heading text-xl text-primary-900 mb-4 pb-2 border-b border-slate-200">10. Postanowienia Końcowe</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Niniejszy dokument stanowi integralną część Regulaminu serwisu KAI.</li>
                <li>Autor zastrzega sobie prawo do aktualizacji niniejszych postanowień. O zmianach Użytkownicy zostaną poinformowani.</li>
                <li>W sprawach nieuregulowanych niniejszym dokumentem zastosowanie mają przepisy ustawy z dnia 4 lutego 1994 r. o prawie autorskim i prawach pokrewnych oraz inne właściwe przepisy prawa polskiego.</li>
                <li>Dokument wchodzi w życie z dniem 7 lutego 2026 r.</li>
              </ol>
            </section>

            {/* Podstawa prawna */}
            <section className="bg-slate-50 p-6 rounded-xl border border-slate-200">
              <div className="flex items-start gap-4">
                <Scale className="w-6 h-6 text-slate-600 dark:text-slate-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading text-lg text-primary-900 dark:text-slate-100 mb-2">Podstawa Prawna</h3>
                  <ul className="text-slate-600 dark:text-slate-400 text-xs space-y-1">
                    <li>• Ustawa z dnia 4 lutego 1994 r. o prawie autorskim i prawach pokrewnych (Dz.U. 2022 poz. 2509)</li>
                    <li>• Ustawa z dnia 30 czerwca 2000 r. Prawo własności przemysłowej (Dz.U. 2021 poz. 324)</li>
                    <li>• Dyrektywa 2001/29/WE w sprawie harmonizacji niektórych aspektów praw autorskich</li>
                    <li>• Konwencja berneńska o ochronie dzieł literackich i artystycznych</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Dane autora */}
            <div className="mt-12 p-6 bg-primary-50 rounded-xl border border-primary-100">
              <h3 className="font-heading text-lg text-primary-900 dark:text-slate-100 mb-2">Właściciel Praw Autorskich</h3>
              <p className="text-slate-700">
                <strong>Monika Małgorzata Gawkowska</strong><br />
                Autor i twórca serwisu KAI (Kurdynowski AI)<br />
                <strong>E-mail:</strong> monika.gawkowska@kurdynowski.pl
              </p>
            </div>

            <p className="text-muted-foreground text-center mt-8">
              © 2026 KAI - Kurdynowski AI. Wszelkie prawa zastrzeżone.<br />
              Autor: Monika Małgorzata Gawkowska
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
